import queryString from 'query-string';
import { useMemo } from 'react';

import { useAlgoliaApiRecommendedJobs } from 'algolia-api/hooks/useAlgoliaApiRecommendedJobs';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { ApiJobFunction } from 'api/types/ApiJobFunction';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { TileCarousel } from 'components/TileCarousel';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { listingSearchHomePath } from 'utils/searchHomePages';

type StandardCmsRecommendedJobsProps = {
  headline: string | null | undefined;
  layout: 'SLIM' | 'REGULAR';
  jobFunction: ApiJobFunction | null | undefined;
  areaOfFocus: ApiAreaOfFocus | null | undefined;
  locale?: UserLocale | 'all';
  source: 'RECOMMENDED_JOBS_CHECKBOX' | 'RECOMMENDED_JOBS_BLOCK';
};

export function CmsStandardRecommendedJobs({
  headline,
  layout,
  jobFunction,
  areaOfFocus,
  locale,
  source,
}: StandardCmsRecommendedJobsProps) {
  const searchLocation = useMainSelector(
    (state) => state.mainSearch.searchLocation,
  ) as SearchLocation;

  const { data: recommendedJobs, isLoading } = useAlgoliaApiRecommendedJobs(
    searchLocation,
    {
      onlyWithLogo: layout === 'REGULAR',
      jobFunction,
      areaOfFocus,
      locale: locale === 'all' ? undefined : locale,
    },
  );

  const additionalAnalyticsProps: Record<string, string> = {};

  if (jobFunction) additionalAnalyticsProps.job_function = jobFunction;
  if (areaOfFocus) additionalAnalyticsProps.area_of_focus = areaOfFocus;

  const seeAllQueryString = useMemo(
    () =>
      queryString.stringify(
        {
          q: '',
          functions: [jobFunction],
          areasOfFocus: [areaOfFocus],
        },
        {
          skipNull: true,
        },
      ),
    [jobFunction, areaOfFocus],
  );

  if (isLoading) return <LoadingIndicator />;

  return (
    <TileCarousel
      headline={headline}
      results={recommendedJobs}
      seeAll={{
        /* TRANSLATORS: Link to job search */
        text: getText('See all jobs'),
        action: {
          type: 'link',
          url: `${listingSearchHomePath('JOB')}?${seeAllQueryString}`,
        },
      }}
      listingType="JOB"
      carouselLocation="CMS Page"
      layout={layout}
      source={source}
      additionalAnalyticsProps={additionalAnalyticsProps}
    />
  );
}
